import moment from "moment";
import companyService from "@/services/company";
import hasPlural from "@/components/report/shared/hasPlural";
import {
  ALL_SHIFTS,
  QUERY_KEYS,
  parseIds,
  parseRange,
  parseShifts,
  serializeIds,
  serializeShifts,
} from "@/components/report/desktop/reportQuery";

const ISO = "YYYY-MM-DD";
/** Ključevi adrese kojima upravlja ljuska izveštaja firme (ostali se ne diraju). */
export const MANAGED_QUERY = ["r", "from", "to", "sh", "c"];

/**
 * Zajedničko stanje ljuske izveštaja firme (telefon i računar): period, smene, firme (filijale naloga),
 * pretraga i adresa. Firme se učitavaju jednom; izbor firmi, period i pretraga idu u store (report), odakle ih
 * čitaju stari tabovi koje ekrani nasleđuju (učitavanje, štampa, Excel). Ljuska koja ga koristi dodaje izbor
 * izveštaja (ključ `r`) i svoj raspored; adresu piše preko `writeUrl(query)`.
 *
 * Prop `canteen` je za stranicu "Izveštaj kantina": firma je nalog u kome je korisnik (bez izbora filijala i bez
 * upita za filijale), a izveštaji su samo Radnici i Preuzeti obroci.
 *
 * Firma "više restorana" (variant "multi") nema filijale nego restorane: "firme" u traci su restorani naloga
 * (store: company.restaurants), izbor ide u report/selectedRestaurants (podrazumevano svi), a smene se upisuju i u
 * filters.selectedShifts, jer ih stari tabovi te firme čitaju odatle.
 */
export default {
  mixins: [hasPlural],
  props: {
    canteen: { type: Boolean, default: false },
  },
  data() {
    const { query } = this.$route;
    const range = parseRange(query[QUERY_KEYS.from], query[QUERY_KEYS.to]);
    const today = moment().format(ISO);
    return {
      from: range ? range[0] : today,
      to: range ? range[1] : today,
      shifts: parseShifts(query[QUERY_KEYS.shifts]) || [...ALL_SHIFTS],
      branches: [],
      branchesLoading: true,
      branchesFailed: false,
    };
  },
  computed: {
    companyName() {
      return this.toLatin(this.$store.state.company?.name || "");
    },
    /** Firma "više restorana": izbor su restorani (ids u store-u), inače firme (filijale) naloga. */
    isMulti() {
      return this.variant === "multi";
    },
    selectedCompanies() {
      if (this.isMulti) {
        const ids = this.$store.state.report.selectedRestaurants || [];
        return this.branches.filter((branch) => ids.includes(branch.id));
      }
      return this.$store.state.report.selectedFoodCompanies || [];
    },
    selectedCompanyIds() {
      return this.selectedCompanies.map((company) => company.id);
    },
    /** Izbor firme ima smisla samo kad nalog ima više filijala. */
    showCompanies() {
      return this.branches.length > 1;
    },
    ready() {
      return !this.branchesLoading && this.selectedCompanyIds.length > 0;
    },
    allCompaniesSelected() {
      return this.branches.length > 0 && this.selectedCompanies.length === this.branches.length;
    },
    companiesLabel() {
      if (this.selectedCompanies.length === 1) {
        return this.toLatin(this.selectedCompanies[0].name);
      }
      if (this.allCompaniesSelected) {
        return this.$t(this.isMulti ? "guest_desktop.all_restaurants_n" : "report_mobile.all_companies_n", { n: this.branches.length });
      }
      return this.pluralT(this.isMulti ? "guest_desktop.restaurants_n" : "report_mobile.companies_n", this.selectedCompanies.length);
    },
    /** Nazivi u izboru stavki: restorani umesto firmi (isti oblik kao u izveštaju gost). */
    companyLabels() {
      if (!this.isMulti) {
        return null;
      }
      return {
        title: this.$t("restaurants"),
        search: this.$t("guest_desktop.search_restaurants"),
        pick: this.$t("select_restaurant"),
        all: (n) => this.$t("guest_desktop.all_restaurants_n", { n }),
        count: (n) => this.pluralT("guest_desktop.restaurants_n", n),
      };
    },
    allShiftsSelected() {
      return this.shifts.length === ALL_SHIFTS.length;
    },
    search: {
      get() {
        return this.$store.state.report.filters.search;
      },
      set(value) {
        this.$store.commit("report/SET_FILTER", { key: "search", value });
      },
    },
    /** Period, smene i firme za adresu (današnji dan, sve smene i prva firma su podrazumevani, pa se izostavljaju). */
    stateQuery() {
      const today = moment().format(ISO);
      const query = {};
      if (!(this.from === today && this.to === today)) {
        query[QUERY_KEYS.from] = this.from;
        query[QUERY_KEYS.to] = this.to;
      }
      const shifts = serializeShifts(this.shifts);
      if (shifts) {
        query[QUERY_KEYS.shifts] = shifts;
      }
      const first = this.branches[0];
      // firme: podrazumevana je prva; restorani: svi (serializeIds tada ništa ne upisuje)
      const isDefault = !this.isMulti && this.selectedCompanyIds.length === 1 && first && this.selectedCompanyIds[0] === first.id;
      const companies = isDefault ? undefined : serializeIds(this.selectedCompanyIds, this.branches.length);
      if (companies) {
        query[QUERY_KEYS.companies] = companies;
      }
      return query;
    },
  },
  watch: {
    "$store.state.company"(company) {
      if (this.canteen) {
        this.useCanteenCompany(company);
      }
    },
    /** Smene iz trake idu i u store, odakle ih čitaju stari tabovi firme "više restorana". */
    shifts: {
      handler(shifts) {
        if (this.isMulti) {
          this.$store.commit("report/SET_FILTER", { key: "selectedShifts", value: [...shifts] });
        }
      },
      immediate: true,
    },
  },
  created() {
    // Store pamti period iz prethodne posete; ekran uvek kreće od perioda koji prikazuje.
    this.commitPeriod();
    this.search = "";
    this.loadBranches();
  },
  beforeDestroy() {
    this.search = "";
  },
  methods: {
    commitPeriod() {
      this.$store.commit("report/SET_DATE_RANGE", { from: this.from, to: this.to });
    },
    /** Firme (filijale) naloga; izbor iz adrese, inače ono što je store zapamtio, inače prva firma. */
    async loadBranches() {
      if (this.canteen) {
        this.useCanteenCompany(this.$store.state.company);
        return;
      }
      if (this.isMulti) {
        this.useCompanyRestaurants();
        return;
      }
      this.branchesLoading = true;
      this.branchesFailed = false;
      try {
        const { data } = await companyService.getBranches({ companies: this.$store.state.company?.id });
        this.branches = (Array.isArray(data) ? data : []).map((item) => ({ id: item[0], name: item[1] }));
        const wanted = parseIds(this.$route.query[QUERY_KEYS.companies]);
        const known = this.selectedCompanyIds;
        const pick = (ids) => this.branches.filter((branch) => ids.includes(branch.id));
        const chosen = pick(wanted).length ? pick(wanted) : pick(known).length ? pick(known) : this.branches.slice(0, 1);
        this.$store.commit("report/SET_SELECTED_FOOD_COMPANIES", chosen);
      } catch (error) {
        this.branches = [];
        this.branchesFailed = true;
      } finally {
        this.branchesLoading = false;
      }
    },
    /**
     * Firma "više restorana": restorani naloga su "firme" u traci; izbor iz adrese, inače ono što je store zapamtio,
     * inače svi restorani (kao u starom izgledu).
     */
    useCompanyRestaurants() {
      const restaurants = (this.$store.state.company && this.$store.state.company.restaurants) || [];
      this.branchesFailed = false;
      this.branches = restaurants.map((restaurant) => ({ id: restaurant.id, name: this.toLatin(restaurant.name || "") }));
      const wanted = parseIds(this.$route.query[QUERY_KEYS.companies]);
      const known = this.$store.state.report.selectedRestaurants || [];
      const pick = (ids) => this.branches.filter((branch) => ids.includes(branch.id)).map((branch) => branch.id);
      const chosen = pick(wanted).length ? pick(wanted) : pick(known).length ? pick(known) : this.branches.map((branch) => branch.id);
      this.$store.commit("report/SET_SELECTED_RESTAURANTS", chosen);
      this.branchesLoading = false;
    },
    /** Kantina: jedina firma je nalog korisnika (isto kao stara stranica); stari tabovi je čitaju iz store-a. */
    useCanteenCompany(company) {
      this.branchesFailed = false;
      this.branches = company ? [{ id: company.id, name: company.name }] : [];
      if (company) {
        this.$store.commit("report/SET_SELECTED_FOOD_COMPANIES", [company]);
      }
      this.branchesLoading = false;
    },
    setPeriod({ from, to }) {
      this.from = from;
      this.to = to;
      this.commitPeriod();
    },
    setCompanies(ids) {
      if (this.isMulti) {
        const valid = this.branches.filter((branch) => ids.includes(branch.id)).map((branch) => branch.id);
        if (valid.join(",") !== this.selectedCompanyIds.join(",")) {
          this.$store.commit("report/SET_SELECTED_RESTAURANTS", valid);
        }
        return;
      }
      if (ids.join(",") !== this.selectedCompanyIds.join(",")) {
        this.$store.commit(
          "report/SET_SELECTED_FOOD_COMPANIES",
          this.branches.filter((branch) => ids.includes(branch.id))
        );
      }
    },
    writeUrl(query) {
      const current = this.$route.query;
      const kept = Object.keys(current).reduce((acc, key) => {
        if (!MANAGED_QUERY.includes(key)) {
          acc[key] = current[key];
        }
        return acc;
      }, {});
      const next = { ...kept, ...query };
      const same =
        Object.keys(next).length === Object.keys(current).length &&
        Object.keys(next).every((key) => String(current[key]) === String(next[key]));
      if (!same) {
        this.$router.replace({ query: next }).catch(() => {});
      }
    },
  },
};
